import clsx from 'clsx';
import ReplyPopover from 'components/Questionsv2/builder/ReplyPopover';
import IntentsCombobox from 'components/comboboxes/IntentCombobox';
import EvaluationPopover from 'components/Templates/builder/Evaluation/EvaluationPopover';
import { CircleMinus } from 'lucide-react';
import type { ComponentProps } from 'react';

import HubertSwitch from 'components/HubertUI/HubertSwitch';
import { selectQuestionInTemplateEditAndUneditable, selectQuestionLanguage } from 'rtk/questionEditorSelector';
import { useQuestionEditorContext } from 'components/Questionsv2/builder/context/create-question.context';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import type { TIntent } from '@hubertai/screening-question-types';
import { removeIntent, updateIntent, updateIntentEnabled } from 'rtk/Question.Editor.Slice';
import UneditableInfoTooltip from '../../../UneditableInfoTooltip';

interface Props extends ComponentProps<'div'> {
  intent: TIntent;
  intentIndex: number;
}

const Intent = ({ intent, intentIndex, ...props }: Props) => {
  const dispatch = useAppDispatch();
  const { questionId } = useQuestionEditorContext();
  const questionLanguage = useAppSelector((state) => selectQuestionLanguage(state, questionId));
  const intents = useAppSelector((state) => state.questionEditor.byId[questionId].intents);
  const inTemplateEditAndUneditable = useAppSelector((state) =>
    selectQuestionInTemplateEditAndUneditable(state, questionId),
  );
  return (
    <div
      {...props}
      className={clsx(
        'flex flex-row justify-between',
        'hover:bg-neutral-purple',
        'hubert-vertical-center',
        'pt-[8px] pr-[12px] pb-[8px] pl-[12px]',
        'rounded-md',
      )}
    >
      <div className={clsx('flex flex-row justify-start gap-4', 'hubert-vertical-center', 'w-full')}>
        <UneditableInfoTooltip disabled={inTemplateEditAndUneditable}>
          <IntentsCombobox
            value={intent.intent}
            onValueChange={(value) => {
              dispatch(updateIntent({ questionId, index: intentIndex, field: 'intent', value }));
            }}
            disabled={inTemplateEditAndUneditable}
            excludeIntents={intents.filter((x) => x.intent !== intent.intent).map((x) => x.intent)}
          />
        </UneditableInfoTooltip>
      </div>

      <div className={clsx('flex flex-row justify-end gap-4', 'hubert-vertical-center')}>
        <ReplyPopover field="intents" fieldIndex={intentIndex} replies={intent.replies[questionLanguage] ?? []} />

        <EvaluationPopover
          allowedTabs={['info', 'warning', 'bonus', 'disqualified']}
          nodeId={''}
          customKey={`${intent.intent}.evaluation`}
          evaluation={{
            name: intent.evaluation,
            bonus_points: intent.bonus_points,
          }}
          isDefault
          onEvaluationChange={(e) => {
            dispatch(updateIntent({ questionId, index: intentIndex, field: 'evaluation', value: e.name }));
            if (e.name === 'bonus') {
              dispatch(updateIntent({ questionId, index: intentIndex, field: 'bonus_points', value: e.bonus_points }));
            }
          }}
          answerType={'intent'}
        />

        <CircleMinus
          className={clsx('cursor-pointer', inTemplateEditAndUneditable && 'hidden')}
          onClick={() => {
            dispatch(removeIntent({ questionId, index: intentIndex }));
          }}
        />

        <UneditableInfoTooltip disabled={inTemplateEditAndUneditable}>
          <HubertSwitch
            disabled={inTemplateEditAndUneditable}
            checked={intent.enabled[questionLanguage]}
            onCheckedChange={(value) => {
              dispatch(updateIntentEnabled({ questionId, intentIndex, enabled: value, language: questionLanguage }));
            }}
          />
        </UneditableInfoTooltip>
      </div>
    </div>
  );
};

export default Intent;
